import Vue from "vue";
import axios from "axios";
import qs from "qs";
import {
  getToken,
  getRefreshToken,
  setToken,
  setRefreshToken,
  removeToken,
  removeRefreshToken,
} from "./plugins/auth";
import { getBaseUrl } from "./api-mapping";
import { isHXApp } from "@saas/components/src";
import { jsToFlutter } from "@saas/components/src/utils/js-to-flutter";
import Router from "@/router";
import {
  buildRequestWithoutToken,
  shouldRetryRequestWithoutToken,
} from "./auth-retry";
let isRefreshing = false;
let refreshPromise = [];
// create an axios instance
const service = axios.create({
  withCredentials: true, // send cookies when cross-domain requests
  timeout: 10000, // request timeout
});
const isElectron = /electron/i.test(navigator.userAgent.toLowerCase());
const isMac = /macintosh|mac os x|macintel/i.test(
  navigator.userAgent.toLowerCase()
);
const xSiteBelong = getXSiteBelong();
if (xSiteBelong) {
  service.defaults.headers["X-Site-Belong"] = xSiteBelong;
}

// 获取 X-Site-Belong 头部值
function getXSiteBelong() {
  if (isElectron && isMac) {
    return "mgyk-desktop-mac";
  } else if (isElectron) {
    return "mgyk-desktop";
  } else if (/mgyk-ios-app-pad/i.test(navigator.userAgent)) {
    return "mgyk-pad";
  } else if (/mgyk-android-app-pad/i.test(navigator.userAgent)) {
    return "mgyk-pad";
  } else if (/mgyk-ios-app/i.test(navigator.userAgent)) {
    return "mgyk-ios";
  } else if (/mgyk-Android-app/i.test(navigator.userAgent)) {
    return "mgyk-android";
  } else {
    return "";
  }
}
// 跳转登录页
function jumpLoginPage() {
  // 当前页面需要登录
  if (Router?.app?._route?.meta?.requireAuth) {
    if (window.isElectron) {
      Router.push({
        name: "LoginPage",
      });
    } else {
      // let preRoute = window.location.href;
      // window.location.href = `${process.env.VUE_APP_ADMIN_URL}/login?return_url=${preRoute}`;
      Vue.prototype.$loginDialog();
    }
  }
}

function getHeaderValue(headers, key) {
  if (!headers) return "";
  const targetKey = Object.keys(headers).find(
    (item) => item.toLowerCase() === key.toLowerCase()
  );
  return targetKey ? headers[targetKey] : "";
}

function getSafeUrl(url = "", baseURL = "") {
  const rawUrl = `${url || ""}`;
  try {
    const parsedUrl = new URL(rawUrl, baseURL || window.location?.origin);
    return `${parsedUrl.origin}${parsedUrl.pathname}`;
  } catch (error) {
    return rawUrl.split("?")[0];
  }
}

function getObjectKeys(value) {
  if (!value || typeof value !== "object") return [];
  if (value instanceof FormData) {
    return Array.from(value.keys());
  }
  return Object.keys(value);
}

function attachAxiosErrorContext(error) {
  if (!error || !error.config) return error;
  const { config, response } = error;
  const method = (config.method || "get").toUpperCase();
  const url = getSafeUrl(config.url, config.baseURL);
  const context = {
    "axios.request.method": method,
    "axios.request.url": url,
    "axios.request.fullUrl": url,
    "axios.request.originalUrl": config.url,
    "axios.request.baseURL": config.baseURL || "",
    "axios.request.timeout": config.timeout,
    "axios.request.contentType": getHeaderValue(config.headers, "Content-Type"),
    "axios.request.paramsKeys": getObjectKeys(config.params),
    "axios.request.dataKeys": getObjectKeys(config.data),
    "axios.response.status": response?.status,
    "axios.response.statusText": response?.statusText,
    "axios.hasResponse": Boolean(response),
    "axios.code": error.code,
  };
  error.__sentry_extra__ = {
    ...error.__sentry_extra__,
    axios: context,
  };
  return error;
}

// request interceptor
service.interceptors.request.use(
  (config) => {
    config.url =
      process.env.NODE_ENV === "development"
        ? config.url
        : getBaseUrl(config.url);
    // do something before request is sent
    // console.log("request>>>", window.isElectron);
    // console.log("request>>>", window.getElectronToken);
    if (window.isElectron && window.getElectronToken && !getToken()) {
      return window.getElectronToken().then((res) => {
        console.log("res>>>", res);
        if (res.token) {
          config.headers.Authorization = res.token;
          setToken(res.token);
          setRefreshToken(res.refreshToken);
        }
        return config;
      });
    } else {
      config.headers.Authorization = getToken();
      return config;
    }
  },
  (error) => {
    // do something with request error
    console.log(error); // for debug
    return Promise.reject(error);
  }
);

// response interceptor
service.interceptors.response.use(
  /**
   * If you want to get http information such as headers or status
   * Please return  response => response
   */

  /**
   * Determine the request status by custom code
   * Here is just an example
   * You can also judge the status by HTTP Status Code
   */
  (response) => {
    const res = response.data;
    // 获取服务器时间
    const serverTime = response.headers["x-server-time"];
    if (serverTime) {
      // 将服务器时间添加到返回数据中
      if (res && typeof res === "object") {
        res.serverTime = serverTime;
      }
    }

    // console.log("urlconfig>>>>", response.config);
    // console.log("getRefreshToken>>>>", getRefreshToken());
    if (res.code === 10018 && getRefreshToken()) {
      // console.log("refreshToken>>>>");
      // console.log("refreshToken isRefreshing>>>>", isRefreshing);
      // token过期, 并且本地有refreshtoken的，
      if (!isRefreshing) {
        // 如果没有正在刷新token， 调用刷新token接口
        isRefreshing = true;
        return refreshToken()
          .then((newTokenData) => {
            if (newTokenData.code === 1) {
              // 刷新token 成功， 更新token， refreshtoken, 并且将挂起的请求重新请求
              setRefreshToken(newTokenData.data.refresh_token);
              setToken(newTokenData.data.access_token);
              response.headers.Authorization = newTokenData.data.access_token;
              refreshPromise.forEach((request) => {
                request(newTokenData.data.access_token);
              });
              refreshPromise = [];
              isRefreshing = false;
              if (isHXApp()) {
                jsToFlutter("refreshToken");
              }
              return service(response.config);
            } else {
              // 如果刷新token 失败，删除token ,refreshtoken
              removeToken();
              removeRefreshToken();
              isRefreshing = false;
              refreshPromise.forEach((request) => {
                request("");
              });
              refreshPromise = [];
              if (Router?.app?._route?.meta?.requireAuth) {
                // 如果当前页面需要登录，跳转登录页面
                jumpLoginPage();
                return newTokenData;
              } else if (shouldRetryRequestWithoutToken(response.config)) {
                return service(buildRequestWithoutToken(response.config));
              } else {
                return newTokenData;
              }
            }
          })
          .catch((error) => {
            console.log("refreshToken error", error);
            isRefreshing = false;
            removeToken();
            removeRefreshToken();
            jumpLoginPage();
          });
      } else {
        // 如果正在刷新token， 将请求存到refreshPromise数组中
        return new Promise((resolve, reject) => {
          refreshPromise.push((token) => {
            response.headers.Authorization = token;
            resolve(service(response.config));
          });
        });
      }
    } else if (res.code === 10018 && !getRefreshToken()) {
      // 如果token过期，并且本地没有refreshtoken，删除token和refreshToken，需要登录的页面跳转登录页，不需要登录的页面，接口不带token重新请求
      removeToken();
      removeRefreshToken();
      if (Router?.app?._route?.meta?.requireAuth) {
        // 需要登录
        jumpLoginPage();
        return res;
      } else if (shouldRetryRequestWithoutToken(response.config)) {
        return service(buildRequestWithoutToken(response.config));
      }
      return res;
    } else if (res.code === 10009) {
      // 未登录
      removeToken();
      removeRefreshToken();
      if (Router?.app?._route?.meta?.requireAuth) {
        // 需要登录
        jumpLoginPage();
        return res;
      } else {
        return res;
      }
    } else if (res.code === 10040) {
      // 设备被移除
      // Vue.prototype.$offlineDialog();
      removeToken();
      removeRefreshToken();
      Vue.prototype.$loginDialog();
    } else if (res.code === 10041) {
      // 被挤下线，需要弹窗
      Vue.prototype.$offlineDialog();
    } else {
      return res;
    }
  },
  (error) => {
    console.log("err>>>" + error); // for debug
    attachAxiosErrorContext(error);
    // Message({
    //   message: error.message,
    //   type: 'error',
    //   duration: 3 * 1000
    // })
    return Promise.reject(error);
  }
);

export const refreshToken = async () => {
  // 使用当前存储的 refresh_token 发送请求
  const api = "/v1/auth/refresh";
  const url = process.env.NODE_ENV === "development" ? api : getBaseUrl(api);
  const refreshTokenResponse = await axios.post(
    url,
    {
      refresh_token: getRefreshToken(),
    },
    {
      headers: {
        "X-Site-Belong": xSiteBelong,
      },
    }
  );
  return refreshTokenResponse.data;
};

Vue.prototype.$http = service;

// 通用的请求
export const request = function request(url, method, options, timeout = 10000) {
  const { params = {}, data = {}, json } = options || {};

  // 创建 AbortController 实例（用于 loading 组件取消请求）
  const controller = new AbortController();
  const signal = controller.signal;
  window.controller = controller;

  return new Promise((resolve, reject) => {
    service.defaults.headers["X-Requested-With"] = "XMLHttpRequest";
    // 注意：不要在全局设置 signal，应该在请求配置中单独设置
    // service.defaults.signal = signal; // ❌ 这会影响所有请求

    service({
      url,
      method,
      params,
      data: json || qs.stringify(data),
      timeout,
      signal, // ✅ 只在当前请求中使用 signal
    }).then(
      (res) => {
        resolve(res);
      },
      (error) => {
        console.log(axios.isCancel(error), "============");
        attachAxiosErrorContext(error);
        reject(error);
      }
    );
  });
};

// contenttype: json: application/json , data: application/x-www-form-urlencoded, file: multipart/form-data
export const post = (
  url,
  data = {},
  contentType = "json",
  customConfig = {}
) => {
  const config = { url };
  // 根据contentType设置请求头
  switch (contentType) {
    case "data":
      config.data = qs.stringify(data);
      break;

    case "json":
      config.data = data;
      break;

    case "file":
      // 对于multipart/form-data，通常需要额外处理，例如使用FormData对象
      var formData = new FormData();
      Object.keys(data).forEach((key) => {
        formData.append(key, data[key]);
      });
      config.data = formData;
      break;

    default:
      throw new Error("Unsupported content type");
  }
  return service.post(config.url, config.data, customConfig);
};

export const get = (url, data = {}) => {
  return service({
    url: url,
    method: "get",
    params: data,
  });
};

export const getJSON = (url) => {
  return service({
    method: "GET",
    url: `${url}?t=${new Date().getTime()}`,
    dataType: "json",
  });
};

export const postFile = (url, params = {}) => {
  let form = new window.FormData();
  Object.keys(params).forEach((item) => {
    if (params[item] instanceof Array) {
      params[item].forEach((ele, index) => {
        form.append(`${item}[]`, ele);
      });
    } else {
      form.append(item, params[item]);
    }
  });
  return service({
    url,
    method: "POST",
    data: form,
  });
};
